import React from 'react';
import {useTranslation} from 'react-i18next';
import ReactGA from 'react-ga';
import styled from 'styled-components';
import {useHistory} from 'react-router';
import {Link} from 'react-router-dom';
import privacy from '../../config/privacy';
import imageUtil from '../../utils/imageUtil';
import AppIcon from '../AppIcon';
import ReviewItem from '../review/reviewItem';
import PublishingSettings from '../../containers/book/detail/publishingSettings';

export const DEFAULT_COVER_BACKGROUND =
  'https://storage.googleapis.com/beta-reader-prod.appspot.com/defaults/DEFAULT_COVER_BACKGROUND.png';

const BookCard = styled.article`
  display: flex;
  gap: 20px;
  padding: 20px 0;
  border-bottom: 1px solid var(--br-border);
  color: var(--br-text);
  cursor: pointer;

  .book-item-cover { width: 80px; height: 116px; object-fit: cover; border-radius: 10px; flex: none; }
  .book-item-content { min-width: 0; flex: 1; }
  .book-item-heading { display: flex; align-items: flex-start; gap: 8px; margin: 0 0 8px; }
  .book-item-heading h3 { flex: 1; min-width: 0; margin: 0; font-size: 1.2rem; }
  .book-item-title { color: inherit; overflow-wrap: anywhere; }
  .book-item-meta { color: var(--br-muted); }
  .book-item-description { max-width: 80ch; margin: 8px 0; overflow-wrap: anywhere; }
  .book-item-metrics { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
  .book-item-metric { display: inline-flex; align-items: center; gap: 5px; min-width: 52px;
    min-height: 44px; padding: 0 10px; border: 1px solid var(--br-border);
    border-radius: 6px; color: var(--br-accent); text-decoration: none; }
  .book-item-metric:hover, .book-item-title:hover { text-decoration: underline; }
  .book-item-collaborator, .book-item-rating { font-size: 0.8rem; color: var(--br-accent); }
  .book-item-privacy { position: relative; flex: none; }
  .book-item-privacy summary { cursor: pointer; list-style: none; min-width: 24px; min-height: 24px; }
  .book-item-privacy summary::-webkit-details-marker { display: none; }
  .book-item-privacy-panel { position: absolute; z-index: 3; top: 100%; left: 0;
    width: min(320px, calc(100vw - 40px)); padding: 12px; background: var(--br-surface);
    border: 1px solid var(--br-border); border-radius: 8px; box-shadow: 0 6px 18px rgba(0,0,0,.15); }
  .book-item-reviews { flex: 0 0 190px; width: 190px; align-self: center; }
  .book-item-rating { display: none; }

  @media (max-width: 767px) {
    .book-item-cover, .book-item-reviews { display: none; }
    .book-item-rating { display: inline-block; margin-top: 8px; }
    .book-item-meta { font-size: 13px; }
    .book-item-length { display: block; }
  }
`;

const BookItem = ({book}) => {
  const history = useHistory();
  const {t} = useTranslation();
  if (!book) return null;

  const blockClick = event => event.stopPropagation();
  const track = action => ReactGA.event({category: 'General', action});
  const openBook = () => {
    history.push(`/books/${book._id}`);
    track('Clicked Book Card');
  };
  const metric = (label, value, path, icon, action) => (
    <Link className='book-item-metric' to={`/books/${book._id}/${path}`}
      aria-label={`${t(label)}: ${value || 0}`} title={t(label)}
      onClick={event => { blockClick(event); track(`Clicked Book ${action}`); }}>
      <AppIcon name={icon} />{value || 0}
    </Link>
  );

  return (
    <BookCard className='manuscript-book-item' onClick={openBook}>
      <img className='book-item-cover' alt=''
        src={imageUtil.getImageUrl({operation: 'width', size: 200,
          originalImageUrl: book?.cover?.highRes?.url ?? DEFAULT_COVER_BACKGROUND})} />
      <div className='book-item-content'>
        <div className='book-item-heading'>
          <details className='book-item-privacy' onClick={blockClick}>
            <summary aria-label={t('PrivacyLevel')}>
              <AppIcon name={privacy.get(book, 'iconName')} />
            </summary>
            <div className='book-item-privacy-panel'>
              <PublishingSettings book={book} />
            </div>
          </details>
          <h3><Link className='book-item-title' to={`/books/${book._id}`}
            onClick={event => { blockClick(event); track('Clicked Book Card'); }}>
            {book.title}
          </Link></h3>
          {book.referenceId !== undefined &&
            <span className='book-item-reference'>({book.referenceId})</span>}
          {book.role === 'collaborator' &&
            <span className='book-item-collaborator'>{t('collaborator')}</span>}
        </div>
        <div className='book-item-meta'>
          {t('by')} {book.authorName}{' '}
          <span className='book-item-length'>
            ({t('ChapterCount', {count: book.chapterCount || 0})},{' '}
            {t('WordCountString', {count: book.wordCount || 0})})
          </span>
        </div>
        {book.description && <p className='book-item-description'>
          {book.description.length > 250 ? `${book.description.substring(0, 250)}...` : book.description}
        </p>}
        <div className='book-item-metrics'>
          {metric('Comments', book.totalComments, 'feedback/comments', 'comment outline', 'feedback/comments')}
          {metric('Followers', book.totalFollowers, 'followers', 'feed', 'followers')}
          {metric('Reviews', book.totalReviews, 'feedback/reviews', 'eye', 'feedback/reviews')}
        </div>
        {book.avgOverAllRating && <span className='book-item-rating'>
          <AppIcon name='star' /> {book.avgOverAllRating.toFixed(1)}
        </span>}
      </div>
      <div className='book-item-reviews'><ReviewItem reviewData={book} t={t} /></div>
    </BookCard>
  );
};

export default BookItem;
